import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Lock,
  LogOut,
  Loader2,
  AlertCircle,
  Stethoscope,
  Upload,
  FileText,
  ChevronRight,
  CheckCircle2,
  X,
} from "lucide-react";
import { useDoctorAuth } from "../hooks/useDoctorAuth";
import {
  doctorClients,
  doctorListLabs,
  doctorUploadLab,
  downloadLab,
} from "../lib/api";
import LabCommentThread from "../components/LabCommentThread";
import LabAuditLog from "../components/LabAuditLog";

/* -------- Login screen -------- */
function LoginScreen({ onLogin, error }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await onLogin(email.trim().toLowerCase(), password);
    setLoading(false);
  };

  return (
    <section className="min-h-screen flex items-center justify-center bg-[var(--lc-cream)] px-5">
      <form
        onSubmit={submit}
        className="w-full max-w-md bg-white border border-[var(--lc-line)] rounded-md p-8 lg:p-10"
        data-testid="doctor-login-form"
      >
        <Link to="/" className="inline-flex items-center gap-2 leading-none mb-7">
          <span className="font-serif-display text-xl tracking-tight">LONGEV</span>
          <span className="w-1.5 h-1.5 rounded-full bg-[var(--lc-moss)]" />
          <span className="font-serif-display italic text-lg">circle</span>
        </Link>
        <div className="w-12 h-12 rounded-full bg-[var(--lc-moss)] grid place-items-center mb-6">
          <Stethoscope className="h-5 w-5 text-white" />
        </div>
        <div className="eyebrow text-[var(--lc-moss)] mb-3">Partner Physician</div>
        <h1 className="font-serif-display text-3xl leading-tight">
          Secure clinician <span className="italic">sign-in.</span>
        </h1>
        <p className="text-sm text-[var(--lc-muted)] mt-3">
          Sign in to upload lab results and private notes against each member of the Circle.
        </p>

        <div className="mt-7 space-y-4">
          <div>
            <label className="eyebrow text-[var(--lc-muted)] block mb-2">Email</label>
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              className="w-full px-4 py-3 bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-sm text-[14px] focus:outline-none focus:border-[var(--lc-ink)]"
              autoFocus
              data-testid="doctor-email-input"
            />
          </div>
          <div>
            <label className="eyebrow text-[var(--lc-muted)] block mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-sm text-sm focus:outline-none focus:border-[var(--lc-ink)]"
              data-testid="doctor-password-input"
            />
          </div>
        </div>

        {error && (
          <div className="mt-4 flex items-center gap-2 px-3 py-2.5 bg-red-50 border border-red-200 text-red-700 text-sm rounded-sm" data-testid="doctor-login-error">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {error}
          </div>
        )}

        <button
          disabled={loading || !email || !password}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 bg-[var(--lc-ink)] text-[var(--lc-cream)] text-[12px] font-bold tracking-[0.18em] uppercase hover:bg-[var(--lc-moss-dark)] transition-colors disabled:opacity-50"
          data-testid="doctor-login-submit"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
          Sign in
        </button>

        <p className="mt-6 text-[11px] text-center text-[var(--lc-muted)] tracking-wide">
          Access is granted by the clinic admin. Having trouble? Contact the admin to reset your password.
        </p>
      </form>
    </section>
  );
}

/* -------- Lab upload modal -------- */
function UploadLabModal({ token, client, onClose, onUploaded }) {
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!file || !title.trim()) return;
    setBusy(true);
    setError("");
    setProgress(0);
    try {
      await doctorUploadLab(
        token,
        client.id,
        { title: title.trim(), notes, file },
        (ev) => {
          if (ev.total) setProgress(Math.round((ev.loaded / ev.total) * 100));
        }
      );
      onUploaded();
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Upload failed";
      setError(typeof detail === "string" ? detail : "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[var(--lc-ink)]/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-lg bg-white border border-[var(--lc-line)] rounded-md p-6 lg:p-8"
        data-testid="lab-upload-modal"
      >
        <div className="flex items-start justify-between gap-3 mb-5">
          <div>
            <div className="eyebrow text-[var(--lc-moss)]">Upload lab result</div>
            <h3 className="font-serif-display text-2xl mt-1">
              For <span className="italic">{client.name}</span>
            </h3>
          </div>
          <button type="button" onClick={onClose} className="text-[var(--lc-muted)] hover:text-red-700" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="eyebrow text-[var(--lc-muted)] block mb-2">Title</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              placeholder="e.g. HbA1c panel — October 2026"
              className="w-full px-4 py-3 bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-sm text-sm focus:outline-none focus:border-[var(--lc-ink)]"
              data-testid="lab-upload-title"
            />
          </div>
          <div>
            <label className="eyebrow text-[var(--lc-muted)] block mb-2">Notes (optional, private to coach + client)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Clinical interpretation, next steps, flags..."
              className="w-full px-4 py-3 bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-sm text-sm focus:outline-none focus:border-[var(--lc-ink)] resize-none"
              data-testid="lab-upload-notes"
            />
          </div>
          <div>
            <label className="eyebrow text-[var(--lc-muted)] block mb-2">File (PDF / JPEG / PNG / WEBP — max 15 MB)</label>
            <input
              type="file"
              accept="application/pdf,image/jpeg,image/png,image/webp"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              required
              className="w-full text-sm text-[var(--lc-muted)] file:mr-3 file:px-4 file:py-2.5 file:border-0 file:bg-[var(--lc-ink)] file:text-white file:text-[11px] file:font-bold file:tracking-[0.14em] file:uppercase hover:file:bg-[var(--lc-moss-dark)]"
              data-testid="lab-upload-file"
            />
          </div>

          {busy && progress > 0 && (
            <div className="w-full h-1.5 bg-[var(--lc-line)] overflow-hidden rounded-full">
              <div className="h-full bg-[var(--lc-moss)]" style={{ width: `${progress}%` }} />
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-sm" data-testid="lab-upload-error">
              <AlertCircle className="h-4 w-4" /> {error}
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2.5 border border-[var(--lc-line)] text-[11px] font-bold tracking-[0.14em] uppercase hover:border-[var(--lc-ink)]">
            Cancel
          </button>
          <button
            type="submit"
            disabled={!file || !title.trim() || busy}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[var(--lc-ink)] text-white text-[11px] font-bold tracking-[0.14em] uppercase hover:bg-[var(--lc-moss-dark)] disabled:opacity-50"
            data-testid="lab-upload-submit"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            {busy ? "Uploading..." : "Upload"}
          </button>
        </div>
      </form>
    </div>
  );
}

/* -------- Client lab panel -------- */
function ClientLabPanel({ token, client, onBack, onLabsChanged }) {
  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setLabs(await doctorListLabs(token, client.id));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.id]);

  const open = async (lab) => {
    try {
      const blob = await downloadLab({ labId: lab.id, doctorToken: token });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      // noop
    }
  };

  return (
    <div className="space-y-5" data-testid="doctor-client-lab-panel">
      <div className="flex items-center justify-between gap-3">
        <div>
          <button onClick={onBack} className="eyebrow text-[var(--lc-muted)] hover:text-[var(--lc-ink)] inline-flex items-center gap-1.5" data-testid="doctor-back">
            ← All members
          </button>
          <h2 className="font-serif-display text-3xl mt-2">{client.name}</h2>
          <div className="text-sm text-[var(--lc-muted)] mt-1">
            {client.email || "No email on file"}
            {client.phone ? ` · ${client.phone}` : ""}
            {client.is_t1d ? " · T1D flagged" : ""}
          </div>
        </div>
        <button
          onClick={() => setModal(true)}
          className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-[var(--lc-ink)] text-white text-[11px] font-bold tracking-[0.14em] uppercase hover:bg-[var(--lc-moss-dark)]"
          data-testid="doctor-upload-open"
        >
          <Upload className="h-3.5 w-3.5" /> Upload lab result
        </button>
      </div>

      <div className="bg-white border border-[var(--lc-line)] rounded-md divide-y divide-[var(--lc-line)]">
        {loading ? (
          <div className="p-8 flex items-center gap-2 text-sm text-[var(--lc-muted)]">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading lab history...
          </div>
        ) : labs.length === 0 ? (
          <div className="p-10 text-center">
            <FileText className="h-7 w-7 mx-auto text-[var(--lc-muted)] mb-2" />
            <div className="font-serif-display text-xl">No lab results yet</div>
            <p className="text-sm text-[var(--lc-muted)] mt-1">Upload the first PDF or scan to start the member's medical file.</p>
          </div>
        ) : (
          labs.map((l) => (
            <article key={l.id} className="p-5" data-testid={`lab-row-${l.id}`}>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-sm bg-[var(--lc-cream)] grid place-items-center flex-shrink-0 mt-1">
                  <FileText className="h-4 w-4 text-[var(--lc-moss-dark)]" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold">{l.title}</div>
                  <div className="text-xs text-[var(--lc-muted)] mt-0.5">
                    Uploaded {new Date(l.uploaded_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                    {l.doctor_name ? ` · by ${l.doctor_name}` : ""}
                    · {Math.round((l.size || 0) / 1024)} KB · {l.content_type}
                  </div>
                  {l.notes && (
                    <p className="mt-2 text-sm text-[var(--lc-ink)] bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-sm p-3 whitespace-pre-wrap">
                      {l.notes}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => open(l)}
                  className="px-4 py-2 border border-[var(--lc-ink)] text-[11px] font-bold tracking-[0.14em] uppercase hover:bg-[var(--lc-ink)] hover:text-white"
                  data-testid={`lab-open-${l.id}`}
                >
                  Open
                </button>
              </div>
              <LabCommentThread labId={l.id} auth={{ doctorToken: token }} placeholder="Reply to the member or coach..." />
              <LabAuditLog labId={l.id} auth={{ doctorToken: token }} />
            </article>
          ))
        )}
      </div>

      {modal && (
        <UploadLabModal
          token={token}
          client={client}
          onClose={() => setModal(false)}
          onUploaded={() => {
            load();
            onLabsChanged?.();
          }}
        />
      )}
    </div>
  );
}

/* -------- Doctor shell -------- */
export default function DoctorPage() {
  const { token, profile, loading, error, login, logout } = useDoctorAuth();
  const [clients, setClients] = useState([]);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [loadingClients, setLoadingClients] = useState(false);

  const loadClients = async () => {
    if (!token) return;
    setLoadingClients(true);
    try {
      setClients(await doctorClients(token));
    } finally {
      setLoadingClients(false);
    }
  };

  useEffect(() => {
    if (token && profile) loadClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, profile?.id]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) =>
      [c.name, c.email, c.phone].filter(Boolean).some((v) => v.toLowerCase().includes(q))
    );
  }, [clients, search]);

  if (loading) {
    return (
      <section className="min-h-screen flex items-center justify-center bg-[var(--lc-cream)]">
        <Loader2 className="h-6 w-6 animate-spin text-[var(--lc-muted)]" />
      </section>
    );
  }

  if (!profile) return <LoginScreen onLogin={login} error={error} />;

  return (
    <section className="bg-[var(--lc-cream)] min-h-screen pb-16">
      <header className="sticky top-0 z-30 bg-[var(--lc-cream)]/95 backdrop-blur-md border-b border-[var(--lc-line)]">
        <div className="mx-auto max-w-[1100px] px-5 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 leading-none">
            <span className="font-serif-display text-xl tracking-tight">LONGEV</span>
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--lc-moss)]" />
            <span className="font-serif-display italic text-base">circle · doctor</span>
          </Link>
          <div className="flex items-center gap-3">
            <div className="hidden sm:block text-right leading-tight">
              <div className="text-sm font-semibold">{profile.name}</div>
              <div className="text-[10px] tracking-[0.18em] uppercase text-[var(--lc-muted)]">
                {profile.specialty || "Physician"}
              </div>
            </div>
            <button
              onClick={logout}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-[11px] font-bold tracking-[0.16em] uppercase bg-[var(--lc-ink)] text-[var(--lc-cream)] hover:bg-[var(--lc-moss-dark)]"
              data-testid="doctor-logout"
            >
              <LogOut className="h-3 w-3" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1100px] px-5 lg:px-8 py-6 lg:py-10">
        {selected ? (
          <ClientLabPanel
            token={token}
            client={selected}
            onBack={() => setSelected(null)}
            onLabsChanged={loadClients}
          />
        ) : (
          <>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-6">
              <div>
                <div className="eyebrow text-[var(--lc-muted)]">Partner physician</div>
                <h1 className="font-serif-display text-4xl mt-1">
                  Member <span className="italic">medical files.</span>
                </h1>
                <p className="text-sm text-[var(--lc-muted)] mt-2 max-w-xl">
                  Pick a member to review or upload a new lab result. All files are private and visible only to the coach and the member.
                </p>
              </div>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, email, phone..."
                className="px-4 py-2.5 bg-white border border-[var(--lc-line)] rounded-sm text-sm focus:outline-none focus:border-[var(--lc-ink)] w-full lg:w-80"
                data-testid="doctor-client-search"
              />
            </div>

            <div className="bg-white border border-[var(--lc-line)] rounded-md divide-y divide-[var(--lc-line)]" data-testid="doctor-client-list">
              {loadingClients ? (
                <div className="p-8 flex items-center gap-2 text-sm text-[var(--lc-muted)]">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading members...
                </div>
              ) : filtered.length === 0 ? (
                <div className="p-10 text-center text-sm text-[var(--lc-muted)]">
                  No matching members.
                </div>
              ) : (
                filtered.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => setSelected(c)}
                    className="w-full p-5 flex items-center justify-between gap-4 text-left hover:bg-[var(--lc-cream)]/50 transition-colors"
                    data-testid={`doctor-client-row-${c.id}`}
                  >
                    <div className="min-w-0">
                      <div className="font-semibold">{c.name}</div>
                      <div className="text-xs text-[var(--lc-muted)] mt-0.5 truncate">
                        {c.email || "No email"}
                        {c.phone ? ` · ${c.phone}` : ""}
                        {c.is_t1d ? " · T1D" : ""}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 flex-shrink-0">
                      {c.labs_count > 0 ? (
                        <span className="inline-flex items-center gap-1 text-[11px] eyebrow text-[var(--lc-moss-dark)]">
                          <CheckCircle2 className="h-3 w-3" /> {c.labs_count} lab{c.labs_count === 1 ? "" : "s"}
                        </span>
                      ) : (
                        <span className="text-[11px] eyebrow text-[var(--lc-muted)]">No labs yet</span>
                      )}
                      <ChevronRight className="h-4 w-4 text-[var(--lc-muted)]" />
                    </div>
                  </button>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
